import { useCallback, useEffect } from "react";
import { useShallow } from "zustand/react/shallow";

import TableSimple from "ds/components/TableSimple";
import Box from "ds/components/Box";
import useTableSort from "components/Table/useTableSort";
import { SearchQueryOrderDirection } from "types/generated";
import useBulkActionsFloatingBarHeight from "components/BulkActions/FloatingBar/hooks/useBulkActionsFloatingBarHeight";
import { selectSelectionIsStarted } from "ds/components/Table/store/useTableSelectionStore/selectors";
import useShiftSelectionManager from "components/BulkActions/useShiftSelectionManager";
import useTypedContext from "hooks/useTypedContext";
import useErrorHandle from "hooks/useErrorHandle";
import NotFoundPage from "components/error/NotFoundPage";
import useCanManageStackRoleBindings from "shared/Stack/access/useCanManageStackRoleBindings";
import PermissionGateButton from "components/PermissionGate/Button";
import useStackRoleBindings from "shared/Stack/Roles/useStackRoleBindings";

import StackSettingsPageLayout from "../components/PageLayout";
import { StackSettingsContextData } from "../Context";
import STACK_ROLES_CELLS from "./Cells";
import {
  STACK_ROLES_FILTERS_STORAGE_KEY,
  STACK_ROLES_SORT_CONFIG,
  STACK_ROLES_COLUMN_ID,
} from "./constants";
import StackRolesEmpty from "./Empty";
import useStackRolesTableStore from "./useStackRolesTableStore";
import { useFilteredRoles } from "./helpers";
import useStackRolesTableSelectionStore from "./useStackRolesTableSelectionStore";
import useStackRolesSelectionOptions from "./useStackRolesSelectionOptions";
import StackRolesBulkActions from "./BulkActions";
import { showManageRolesDrawer } from "./ManageRolesDrawer";
import styles from "./styles.module.css";
import StackSettingsRolesAdministrativeBanner from "./AdministrativeBanner";

const StackSettingsRoles = () => {
  const { stackSettings } = useTypedContext(StackSettingsContextData);
  const canManageStackRoleBindings = useCanManageStackRoleBindings();
  const canManage = canManageStackRoleBindings(stackSettings);

  const { stackRoleBindings, loading, hasData, error, refetch } = useStackRoleBindings({
    stackID: stackSettings.id,
  });

  const roles = stackRoleBindings;
  const loadingContent = loading && !hasData;

  const { sortDescriptor } = useTableSort({
    filtersOrderSettingsKey: STACK_ROLES_FILTERS_STORAGE_KEY,
    sortConfig: STACK_ROLES_SORT_CONFIG,
    initialSortOption: STACK_ROLES_SORT_CONFIG[STACK_ROLES_COLUMN_ID.ROLE],
    initialSortDirection: SearchQueryOrderDirection.Asc,
  });

  const sortedRoles = useFilteredRoles(roles, sortDescriptor);

  const { selectionOptions, stackRolesMap } = useStackRolesSelectionOptions(sortedRoles);

  const { isBulkActionsActive, toggle, clearSelected, selectedSet, setAllItemsCount } =
    useStackRolesTableSelectionStore(
      useShallow((state) => ({
        isBulkActionsActive: selectSelectionIsStarted(state),
        toggle: state.toggle,
        clearSelected: state.clearSelected,
        selectedSet: state.selected,
        replaceSelected: state.replaceSelected,
        setAllItemsCount: state.setAllItemsCount,
      }))
    );

  // Update the selection store with the total count
  useEffect(() => {
    setAllItemsCount(sortedRoles.length);
  }, [setAllItemsCount, sortedRoles.length]);

  // Clear selections when component unmounts
  useEffect(() => {
    return () => {
      clearSelected();
    };
  }, [clearSelected]);

  const { height: floatingBarHeight, setHeight: setFloatingBarHeight } =
    useBulkActionsFloatingBarHeight(isBulkActionsActive);

  const shiftSelectionAction = useShiftSelectionManager({
    collection: sortedRoles,
    selectedIds: selectedSet,
  });

  const handleManageRoles = () => {
    showManageRolesDrawer({ stackId: stackSettings.id });
  };

  const onBulkActionsFinish = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const hasRoles = sortedRoles.length > 0;

  const actions = (
    <PermissionGateButton access={canManage} variant="primary" onPress={handleManageRoles}>
      Manage roles
    </PermissionGateButton>
  );

  const ErrorContent = useErrorHandle(error);
  if (ErrorContent) {
    return ErrorContent;
  }

  if (!loading && !hasData) {
    return <NotFoundPage />;
  }

  return (
    <>
      {stackSettings.administrative && <StackSettingsRolesAdministrativeBanner />}
      <StackSettingsPageLayout
        title="Roles"
        description="Assign roles to let this stack perform actions outside its own space."
        actions={actions}
      >
        <Box className={styles.wrapper} direction="column" grow="1" fullHeight>
          {(hasRoles || loadingContent) && (
            <TableSimple
              data={sortedRoles}
              loading={loadingContent}
              store={useStackRolesTableStore}
              selectionStore={useStackRolesTableSelectionStore}
              selectionOptions={selectionOptions}
              shiftSelectionAction={shiftSelectionAction}
              ariaLabel="Stack roles table"
              cellPerColumn={STACK_ROLES_CELLS}
              filtersStorageKey={STACK_ROLES_FILTERS_STORAGE_KEY}
              sortConfig={STACK_ROLES_SORT_CONFIG}
              hasPagination={false}
              bottomOffset={floatingBarHeight}
            />
          )}
          {!hasRoles && !loadingContent && (
            <StackRolesEmpty onManageRoles={handleManageRoles} canManage={canManage} />
          )}

          <Box fullWidth relative>
            <StackRolesBulkActions
              selectedSet={selectedSet}
              stackRolesMap={stackRolesMap}
              onBulkResetAll={clearSelected}
              onItemDismiss={toggle}
              onFinish={onBulkActionsFinish}
              onHeightChange={setFloatingBarHeight}
            />
          </Box>
        </Box>
      </StackSettingsPageLayout>
    </>
  );
};

export default StackSettingsRoles;
